import React from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../utils/formatters";
import {
  PLACEHOLDER_CARD_EXPLANATION,
  PLACEHOLDER_CARD_TITLE,
  hiddenPlaceholdersNote,
  partitionSubscriptions,
} from "../../utils/subscriptionPlaceholders";
import type {
  ContactSubscription,
  HouseholdMember,
} from "../../services/contacts.service";

interface ContactAccountSectionProps {
  subscriptions: ContactSubscription[];
  householdMembers: HouseholdMember[];
  currentContactId?: string;
}

const SUB_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  expired: "bg-gray-100 text-gray-700",
  pending: "bg-yellow-100 text-yellow-800",
  suspended: "bg-orange-100 text-orange-800",
};

function StatusBadge({ status }: { status: string | undefined | null }) {
  if (!status) {
    return null;
  }
  const key = status.toLowerCase();
  const colorClass = SUB_STATUS_COLORS[key] ?? "bg-gray-100 text-gray-700";
  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${colorClass}`}
    >
      {status}
    </span>
  );
}

const ContactAccountSection: React.FC<ContactAccountSectionProps> = ({
  subscriptions,
  householdMembers,
}) => {
  const hasData = subscriptions.length > 0 || householdMembers.length > 0;
  // June 2026 bulk-import placeholders (SYNTH-…, no product) are hidden next to a real
  // subscription, and collapsed into ONE explained card when they are all there is.
  const { visible, hiddenPlaceholders, placeholderOnly } =
    partitionSubscriptions(subscriptions);

  if (!hasData) {
    return (
      <p className="text-sm text-gray-400 italic">
        No account or subscription data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Subscriptions */}
      {subscriptions.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Subscriptions
          </h3>
          <div className="space-y-4">
            {placeholderOnly && (
              <div
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                data-testid="subscription-placeholder-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900">
                    {PLACEHOLDER_CARD_TITLE}
                  </p>
                  <StatusBadge status={placeholderOnly.status} />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {PLACEHOLDER_CARD_EXPLANATION}
                </p>
              </div>
            )}
            {visible.map((sub) => (
              <div
                key={sub.id}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                {/* Header row */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {sub.product_names?.join(", ") ||
                        sub.subscription_type ||
                        "Subscription"}
                    </p>
                    {sub.subscription_number && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        #{sub.subscription_number}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={sub.status} />
                </div>

                {/* Details grid */}
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                  {sub.term_start_date && (
                    <div>
                      <dt className="text-gray-500 text-xs">Term Start</dt>
                      <dd className="text-gray-900">
                        {formatDate(sub.term_start_date)}
                      </dd>
                    </div>
                  )}
                  {sub.term_end_date && (
                    <div>
                      <dt className="text-gray-500 text-xs">Term End</dt>
                      <dd className="text-gray-900">
                        {formatDate(sub.term_end_date)}
                      </dd>
                    </div>
                  )}
                  {sub.payment_frequency && (
                    <div>
                      <dt className="text-gray-500 text-xs">Payment</dt>
                      <dd className="text-gray-900 capitalize">
                        {sub.payment_frequency}
                      </dd>
                    </div>
                  )}
                  {sub.region && (
                    <div>
                      <dt className="text-gray-500 text-xs">Region</dt>
                      <dd className="text-gray-900">{sub.region}</dd>
                    </div>
                  )}
                  {sub.auto_renew !== undefined && sub.auto_renew !== null && (
                    <div>
                      <dt className="text-gray-500 text-xs">Auto Renew</dt>
                      <dd className="text-gray-900">
                        {sub.auto_renew ? "Yes" : "No"}
                      </dd>
                    </div>
                  )}
                  {sub.subscription_source && (
                    <div>
                      <dt className="text-gray-500 text-xs">Source</dt>
                      <dd className="text-gray-900 capitalize">
                        {sub.subscription_source}
                      </dd>
                    </div>
                  )}
                </dl>

                {sub.cancellation_reason && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <dt className="text-xs text-gray-500">
                      Cancellation Reason
                    </dt>
                    <dd className="text-sm text-red-700 mt-0.5">
                      {sub.cancellation_reason}
                    </dd>
                  </div>
                )}
              </div>
            ))}
          </div>
          {hiddenPlaceholders.length > 0 && (
            <p
              className="text-xs text-gray-400 mt-2"
              data-testid="subscription-placeholders-hidden-note"
            >
              {hiddenPlaceholdersNote(hiddenPlaceholders)}
            </p>
          )}
        </div>
      )}

      {/* Household Members */}
      {householdMembers.length > 0 && (
        <div
          className={
            subscriptions.length > 0 ? "border-t border-gray-100 pt-4" : ""
          }
        >
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Household Members
          </h3>
          <ul className="space-y-2">
            {householdMembers.map((member) => (
              <li
                key={member.id}
                className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Link
                    to={`/contacts/${member.id}`}
                    className="text-sm font-medium text-primary-600 hover:text-primary-800"
                  >
                    {member.first_name} {member.last_name}
                  </Link>
                  {member.is_primary_member !== undefined &&
                    member.is_primary_member !== null && (
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          member.is_primary_member
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {member.is_primary_member ? "Primary" : "Secondary"}
                      </span>
                    )}
                </div>
                {member.member_status && (
                  <StatusBadge status={member.member_status} />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ContactAccountSection;
